/** @format */

import {type ActiveMediaplan, buildMetricInput} from 'pages/HomeOverview/helpers/buildMetricInput';

import {calcMetricProgress} from 'features/mediaplan-progress/helpers/calcMetricProgress';
import {type IsoDate} from 'features/mediaplan-progress/types';

import {WIDGETS_LIST} from 'pages/Home/DigitalCast/constants/widgets';
import getChartIdByKpiName from 'pages/Home/DigitalCast/helpers/getChartIdByKpiName';
import {type ChartIds} from 'pages/Home/DigitalCast/models/chartIds';

import {type CardBodyCompany} from 'pages/HomeOverview/types';

import {type KpiChartRow} from './KpiSection/KpiCharts';
import {buildChartData} from './buildChartData';
import {kpiChartHeight} from './chartHeights';
import {shouldShowForecast} from './shouldShowForecast';

/**
 * Собирает уникальные chartId всех KPI активной карточки из company.mediaplans[].kpis,
 * маппя имя KPI с бэка на chartId DigitalCast. Дедуп с сохранением порядка появления.
 * Невалидные имена KPI (getChartIdByKpiName бросает) пропускаются.
 */
export const collectKpiChartIds = (company: CardBodyCompany): ChartIds[] => {
  const seen = new Set<ChartIds>();
  const ids: ChartIds[] = [];

  company.mediaplans.forEach(plan => {
    (plan.kpis ?? []).forEach(kpi => {
      let chartId: ChartIds;
      try {
        chartId = getChartIdByKpiName(kpi.name);
      } catch {
        return;
      }
      if (!seen.has(chartId)) {
        seen.add(chartId);
        ids.push(chartId);
      }
    });
  });

  return ids;
};

/**
 * Builds the KPI rows (label, kind, progress, lowerIsBetter, chartId, chart) for the card body.
 * Each row carries everything KpiTable (summary) and KpiCharts (dynamic) need.
 *
 * - `label`: WIDGETS_LIST[chartId].label
 * - `lowerIsBetter`: WIDGETS_LIST[chartId].isPositiveRed === true (cost-type KPIs)
 * - `kind`: additive if the chart has no secondaryPredicate-backed ratio, ratio otherwise
 * - `chartId`: carried on the row (KpiRowModel) for the chart + spend-row icon
 * - `chart`: dynamic-mode chart payload (undefined when there is nothing to plot)
 *
 * `withChart` gates the chart build: summary mode (KpiTable) never reads `row.chart`, so skip the
 * per-KPI buildChartData (calcMetricForecast + point arrays) entirely there — only dynamic needs it.
 */
export const buildKpiRows = (
  company: CardBodyCompany,
  today: IsoDate,
  withChart: boolean,
  active?: ActiveMediaplan | null,
): KpiChartRow[] => {
  const chartIds = collectKpiChartIds(company);
  const rows: KpiChartRow[] = [];

  chartIds.forEach(chartId => {
    const input = buildMetricInput({company, metric: chartId, today, active});
    if (!input) {
      return;
    }

    const widget = WIDGETS_LIST[chartId];
    const label = widget.label;
    const progress = calcMetricProgress(input);
    // <7 days of fact → no forecast (tail + «Прогноз» row).
    const showForecast = shouldShowForecast(input);
    // Chart fills its row → height grows with the KPI's text rows (additive vs ratio). Built only
    // for the dynamic view; summary skips it (the heavy forecast + point arrays would be discarded).
    const chart = withChart
      ? (buildChartData(input, chartId, label, today, kpiChartHeight(input.kind), showForecast) ??
        undefined)
      : undefined;

    rows.push({
      chartId,
      label,
      kind: input.kind,
      progress,
      lowerIsBetter: widget.isPositiveRed === true,
      showForecast,
      // Unit token from the widget: '₽' (money), '%' (ДРР/CTR/ROMI…), undefined (counts).
      unit: widget.token,
      unitNeedsSpace: widget.needSpaceBeforeToken,
      chart,
    });
  });

  return rows;
};
